import React, { useState, useEffect, useCallback } from 'react';
import { Search, Calendar, Clock, X, UserCircle, ArrowLeftRight, PackageCheck } from 'lucide-react';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { useAuth } from '../../../hooks/useAuth';
import { toast } from 'react-hot-toast';
import { resourceService, hardwareService } from '..';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';

const inp = 'w-full px-3 py-2.5 rounded-xl border text-sm outline-none transition-all';
const inpStyle = { borderColor: SYSTEM_COLORS.border, backgroundColor: SYSTEM_COLORS.shellSurface, color: NEUTRAL_COLORS.black };
const Label = ({ children }) => (
  <label className="block text-xs font-semibold mb-1.5" style={{ color: SYSTEM_COLORS.textMuted }}>{children}</label>
);
const Spinner = () => (
  <div className="flex items-center justify-center py-12">
    <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: BRAND_COLORS.primary, borderTopColor: 'transparent' }} />
  </div>
);

const borrowingStatusStyle = (status) => {
  switch (status) {
    case 'active':   return { backgroundColor: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success };
    case 'pending':  return { backgroundColor: SYSTEM_COLORS.warningTint, color: SYSTEM_COLORS.warning };
    case 'rejected': return { backgroundColor: SYSTEM_COLORS.dangerTint,  color: SYSTEM_COLORS.danger };
    case 'returned': return { backgroundColor: SYSTEM_COLORS.infoTint,    color: SYSTEM_COLORS.info };
    default:         return { backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted };
  }
};

const filterBtnStyle = (active) => ({
  backgroundColor: active ? BRAND_COLORS.primary : SYSTEM_COLORS.surfaceMuted,
  color: active ? '#fff' : SYSTEM_COLORS.textMuted,
});

// ─── Borrow Modal ─────────────────────────────────────────────────────────────

const BorrowingModal = ({ isOpen, onClose, hardware, onSubmit }) => {
  const [form, setForm] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: '', quantity: 1, purpose: '',
  });

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.endDate) { toast.error('Select a return date'); return; }
    onSubmit(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ backgroundColor: SYSTEM_COLORS.shellOverlay }}>
      <div className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col max-h-[92dvh] shadow-lg" style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}>
        <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg" style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}>
              <ArrowLeftRight className="w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
            </div>
            <div>
              <h2 className="text-base font-semibold" style={{ color: NEUTRAL_COLORS.black }}>Borrow Hardware</h2>
              <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textSoft }}>{hardware.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-lg hover:opacity-70" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted }}>
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1">
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Borrow Date</Label>
                <input type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} min={new Date().toISOString().split('T')[0]} className={inp} style={inpStyle} required />
              </div>
              <div>
                <Label>Return Date</Label>
                <input type="date" value={form.endDate} onChange={e => setForm({ ...form, endDate: e.target.value })} min={form.startDate} className={inp} style={inpStyle} required />
              </div>
            </div>
            <div>
              <Label>Quantity</Label>
              <input type="number" min={1} max={hardware.status?.available} value={form.quantity} onChange={e => setForm({ ...form, quantity: parseInt(e.target.value) || 1 })} className={inp} style={inpStyle} required />
              <p className="text-xs mt-1" style={{ color: SYSTEM_COLORS.textMuted }}>Available: {hardware.status?.available ?? '—'}</p>
            </div>
            <div>
              <Label>Purpose *</Label>
              <textarea rows={3} value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} placeholder="Describe why you need this item…" className={inp} style={inpStyle} required />
            </div>
          </div>

          <div className="flex justify-end gap-3 px-5 py-4 border-t flex-shrink-0" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
            <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-semibold border hover:opacity-70" style={{ borderColor: SYSTEM_COLORS.border, color: SYSTEM_COLORS.textMuted }}>Cancel</button>
            <button type="submit" className="px-5 py-2.5 rounded-xl text-sm font-semibold hover:opacity-90" style={{ backgroundColor: BRAND_COLORS.primary, color: '#fff' }}>Submit Request</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Resource Flow (admin) ────────────────────────────────────────────────────

const ResourceFlowSection = ({ hardware }) => {
  const [allBorrowings, setAllBorrowings] = useState([]);
  const [searchTerm, setSearchTerm]       = useState('');
  const [filterStatus, setFilterStatus]   = useState('all');
  const [loading, setLoading]             = useState(true);

  const load = useCallback(async () => {
    try {
      setAllBorrowings(await resourceService.getAllBorrowings());
    } catch {
      toast.error('Failed to load resource flow');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (id, status) => {
    try {
      await resourceService.updateBorrowing(id, { status });
      toast.success('Status updated');
      load();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const filtered = allBorrowings.filter(b => {
    const hw = hardware.find(h => h.id === b.hardwareId);
    if (!hw) return false;
    const q = searchTerm.toLowerCase();
    const matchSearch = hw.name?.toLowerCase().includes(q) || hw.category?.toLowerCase().includes(q) || b.userId?.toLowerCase().includes(q);
    return matchSearch && (filterStatus === 'all' || b.status === filterStatus);
  });

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: BRAND_COLORS.primary }}>Resource Flow</p>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: SYSTEM_COLORS.textSoft }} />
          <input type="text" placeholder="Search by name, category, or user…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm outline-none" style={inpStyle} />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'pending', 'active', 'returned'].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} className="px-3 py-1.5 rounded-xl text-xs font-semibold capitalize transition-opacity hover:opacity-80" style={filterBtnStyle(filterStatus === s)}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-32 text-center">
          <PackageCheck className="w-8 h-8 mb-2" style={{ color: SYSTEM_COLORS.border }} />
          <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>No borrowings match your filter</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(b => {
            const hw = hardware.find(h => h.id === b.hardwareId);
            if (!hw) return null;
            return (
              <div key={b.id} className="flex items-start gap-4 p-4 rounded-xl border shadow-md" style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
                {hw.images?.length > 0 ? (
                  <img src={hw.images[0]} alt={hw.name} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
                    <ArrowLeftRight className="w-5 h-5" style={{ color: SYSTEM_COLORS.border }} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-semibold text-sm" style={{ color: NEUTRAL_COLORS.black }}>{hw.name}</p>
                      <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>{hw.category}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {b.status === 'pending' && (
                        <>
                          <button onClick={() => handleStatusChange(b.id, 'active')} className="px-3 py-1 rounded-xl text-xs font-semibold hover:opacity-90" style={{ backgroundColor: SYSTEM_COLORS.success, color: '#fff' }}>Approve</button>
                          <button onClick={() => handleStatusChange(b.id, 'rejected')} className="px-3 py-1 rounded-xl text-xs font-semibold hover:opacity-90" style={{ backgroundColor: SYSTEM_COLORS.danger, color: '#fff' }}>Reject</button>
                        </>
                      )}
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full capitalize" style={borrowingStatusStyle(b.status)}>{b.status}</span>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                    <span className="flex items-center gap-1 text-xs" style={{ color: SYSTEM_COLORS.textMuted }}><UserCircle size={12} /> {b.userId}</span>
                    <span className="flex items-center gap-1 text-xs" style={{ color: SYSTEM_COLORS.textMuted }}><Calendar size={12} /> {b.startDate ? new Date(b.startDate).toLocaleDateString() : '—'} → {b.endDate ? new Date(b.endDate).toLocaleDateString() : '—'}</span>
                    <span className="flex items-center gap-1 text-xs" style={{ color: SYSTEM_COLORS.textMuted }}><Clock size={12} /> Qty: {b.quantity}</span>
                  </div>
                  {b.purpose && <p className="text-xs mt-1.5 pt-1.5 border-t" style={{ color: SYSTEM_COLORS.textMuted, borderColor: SYSTEM_COLORS.borderSoft }}>{b.purpose}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Borrowing Section ────────────────────────────────────────────────────────

const BorrowingSection = () => {
  const { user } = useApiAuth();
  const { hasPermission } = useAuth();
  const [hardware, setHardware]             = useState([]);
  const [borrowings, setBorrowings]         = useState([]);
  const [searchTerm, setSearchTerm]         = useState('');
  const [filterStatus, setFilterStatus]     = useState('all');
  const [selectedHardware, setSelectedHardware] = useState(null);
  const [isModalOpen, setIsModalOpen]       = useState(false);
  const [activeView, setActiveView]         = useState('borrowing');
  const [loading, setLoading]               = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [hw, br] = await Promise.all([
        resourceService.getResources('hardware'),
        resourceService.getBorrowings(user.id),
      ]);
      setHardware(hw);
      setBorrowings(br);
    } catch {
      toast.error('Failed to load borrowing data');
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { load(); }, [load]);

  const handleBorrow = async (formData) => {
    try {
      await hardwareService.checkoutHardware(selectedHardware.id, {
        expectedReturnDate: formData.endDate,
        purpose: formData.purpose,
      });
      toast.success('Borrowing request submitted');
      setIsModalOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? 'Failed to submit request');
    }
  };

  const handleReturn = async (borrowingId, hardwareId) => {
    try {
      if (hardwareId) {
        await hardwareService.checkinHardware(hardwareId, { returnCondition: 'good' });
      } else {
        await resourceService.updateBorrowing(borrowingId, { status: 'returned' });
      }
      toast.success('Item marked as returned');
      load();
    } catch {
      toast.error('Failed to return item');
    }
  };

  const availableHardware = hardware.filter(item => {
    const q = searchTerm.toLowerCase();
    return (item.name?.toLowerCase().includes(q) || item.category?.toLowerCase().includes(q)) && item.status.available > 0;
  });

  const filteredBorrowings = borrowings.filter(b => filterStatus === 'all' || b.status === filterStatus);

  const isAdmin = hasPermission(['admin', 'supervisor', 'resourceManager']);

  return (
    <div className="space-y-4">
      {/* Sub-tab bar (admin only) */}
      {isAdmin && (
        <div className="flex gap-1 border-b" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          {[{ id: 'borrowing', label: 'Borrowing' }, { id: 'flow', label: 'Resource Flow' }].map(({ id, label }) => {
            const active = activeView === id;
            return (
              <button
                key={id}
                onClick={() => setActiveView(id)}
                className="px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors"
                style={{ borderBottomColor: active ? BRAND_COLORS.primary : 'transparent', color: active ? BRAND_COLORS.primary : SYSTEM_COLORS.textMuted }}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {activeView === 'flow' && isAdmin ? (
        loading ? <Spinner /> : <ResourceFlowSection hardware={hardware} />
      ) : (
        <>
          {loading ? <Spinner /> : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Available hardware */}
              <div className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: BRAND_COLORS.primary }}>Available Hardware</p>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: SYSTEM_COLORS.textSoft }} />
                  <input type="text" placeholder="Search available hardware…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm outline-none" style={inpStyle} />
                </div>

                {availableHardware.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 text-center">
                    <ArrowLeftRight className="w-7 h-7 mb-2" style={{ color: SYSTEM_COLORS.border }} />
                    <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>No hardware available</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {availableHardware.map(item => (
                      <div key={item.id} className="flex items-center gap-3 p-3 rounded-xl border shadow-md" style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
                        {item.images?.length > 0 ? (
                          <img src={item.images[0]} alt={item.name} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-14 h-14 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
                            <ArrowLeftRight className="w-5 h-5" style={{ color: SYSTEM_COLORS.border }} />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate" style={{ color: NEUTRAL_COLORS.black }}>{item.name}</p>
                          <p className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>{item.category}</p>
                          <p className="text-xs font-semibold mt-0.5" style={{ color: SYSTEM_COLORS.success }}>{item.status.available} available</p>
                        </div>
                        <button
                          onClick={() => { setSelectedHardware(item); setIsModalOpen(true); }}
                          className="flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-semibold hover:opacity-90 transition-opacity"
                          style={{ backgroundColor: BRAND_COLORS.primary, color: '#fff' }}
                        >
                          Borrow
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* My borrowings */}
              <div className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: BRAND_COLORS.primary }}>My Borrowings</p>

                <div className="flex gap-2 flex-wrap">
                  {['all', 'active', 'pending', 'returned'].map(s => (
                    <button key={s} onClick={() => setFilterStatus(s)} className="px-3 py-1.5 rounded-xl text-xs font-semibold capitalize transition-opacity hover:opacity-80" style={filterBtnStyle(filterStatus === s)}>
                      {s}
                    </button>
                  ))}
                </div>

                {filteredBorrowings.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-32 text-center">
                    <PackageCheck className="w-7 h-7 mb-2" style={{ color: SYSTEM_COLORS.border }} />
                    <p className="text-sm" style={{ color: SYSTEM_COLORS.textMuted }}>No borrowings yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredBorrowings.map(b => {
                      const hw = hardware.find(h => h.id === b.hardwareId);
                      if (!hw) return null;
                      return (
                        <div key={b.id} className="flex items-center gap-3 p-3 rounded-xl border shadow-md" style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
                          {hw.images?.length > 0 ? (
                            <img src={hw.images[0]} alt={hw.name} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-14 h-14 rounded-xl flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
                              <ArrowLeftRight className="w-5 h-5" style={{ color: SYSTEM_COLORS.border }} />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate" style={{ color: NEUTRAL_COLORS.black }}>{hw.name}</p>
                            <div className="flex items-center gap-1 text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>
                              <Calendar size={11} />
                              <span>{b.startDate ? new Date(b.startDate).toLocaleDateString() : '—'} → {b.endDate ? new Date(b.endDate).toLocaleDateString() : '—'}</span>
                            </div>
                            <div className="flex items-center gap-1 text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
                              <Clock size={11} />
                              <span>Qty: {b.quantity}</span>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2 flex-shrink-0">
                            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full capitalize" style={borrowingStatusStyle(b.status)}>{b.status}</span>
                            {b.status === 'active' && (
                              <button onClick={() => handleReturn(b.id, b.hardwareId)} className="px-3 py-1 rounded-xl text-xs font-semibold hover:opacity-90" style={{ backgroundColor: BRAND_COLORS.primary, color: '#fff' }}>
                                Return
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {isModalOpen && selectedHardware && (
        <BorrowingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} hardware={selectedHardware} onSubmit={handleBorrow} />
      )}
    </div>
  );
};

export default BorrowingSection;
